import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { createSepayQr, BillingCycle } from '@/lib/sepay';
import type { Plan } from '@/types';

export function Pricing() {
  const { user, isAdmin } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [qrData, setQrData] = useState<{ qr_url: string; reference: string; amount: number; expires_at: number } | null>(null);
  const [qrError, setQrError] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await api.getPlans();
      setPlans(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tải bảng giá');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQr = async (planId: string) => {
    if (!user) return;
    setQrError('');
    setSelectedPlan(planId);
    try {
      const plan = plans.find(p => p.id === planId);
      if (!plan) throw new Error('Plan not found');
      const cycle: BillingCycle = 'monthly';
      const data = await createSepayQr(planId, user.id, plan.priceMonthly, cycle);
      setQrData(data);
    } catch (err) {
      setQrError(err instanceof Error ? err.message : 'Tạo QR thất bại');
    }
  };

  const handleCloseQr = () => {
    setQrData(null);
    setSelectedPlan(null);
    setQrError('');
  };

  const formatPrice = (price: number, cycle: string) => {
    if (price === 0) return 'Miễn phí';
    const formatted = new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
    return `${formatted}/${cycle === 'monthly' ? 'tháng' : 'năm'}`;
  };

  const formatFeatures = (features: string[]) => {
    const featureLabels: Record<string, string> = {
      'ocr_basic': 'OCR cơ bản',
      'ocr_advanced': 'OCR nâng cao (multi-model)',
      'batch_processing': 'Xử lý hàng loạt',
      'google_sheets': 'Tích hợp Google Sheets',
      'history_search': 'Lịch sử & tìm kiếm',
      'categories': 'Phân loại hóa đơn',
      'api_access': 'API access',
      'priority_support': 'Hỗ trợ ưu tiên',
      'custom_fields': 'Trường tùy chỉnh',
      'webhook': 'Webhook',
      'sso': 'SSO',
      'dedicated_support': 'Hỗ trợ chuyên biệt',
    };
    return features.map(f => featureLabels[f] || f);
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20">
        <div className="container">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-20 bg-gray-50">
      <div className="container">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">Bảng giá</h1>
          <p className="text-lg text-text-muted max-w-2xl mx-auto">
            Chọn gói phù hợp với nhu cầu của bạn. Tất cả gói đều dùng thử miễn phí 7 ngày.
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 text-red-600 bg-red-50 rounded-lg text-center" role="alert">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={user?.planId === plan.id}
              isAdmin={isAdmin}
              onUpgrade={() => handleCreateQr(plan.id)}
              formatPrice={formatPrice}
              formatFeatures={formatFeatures}
            />
          ))}
        </div>

        {/* QR Code Modal */}
        {qrData && selectedPlan && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={handleCloseQr}>
            <div className="card w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={handleCloseQr}
                className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100 text-text-muted"
                aria-label="Đóng"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <h2 className="text-xl font-bold text-text mb-2 text-center">Thanh toán nâng cấp</h2>
              <p className="text-text-muted text-center mb-4">
                Quét mã QR để thanh toán <strong>{formatPrice(plans.find(p => p.id === selectedPlan)?.priceMonthly ?? 0, 'monthly')}</strong>
              </p>

              {qrError && (
                <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-lg" role="alert">
                  {qrError}
                </div>
              )}

              <div className="text-center mb-4">
                <img
                  src={qrData.qr_url}
                  alt={`QR code thanh toán ${qrData.reference}`}
                  className="w-48 h-48 mx-auto border border-border rounded-lg bg-white p-2"
                />
              </div>

              <div className="space-y-2 text-sm text-text-muted mb-4">
                <p><strong>Nội dung:</strong> {qrData.reference}</p>
                <p><strong>Số tiền:</strong> {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(qrData.amount)}</p>
                <p><strong>Hết hạn:</strong> {new Date(qrData.expires_at).toLocaleString('vi-VN')}</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg text-sm text-text-muted">
                <p className="font-medium mb-1">Hướng dẫn:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Mở app ngân hàng / SePay / MoMo / ZaloPay</li>
                  <li>Quét mã QR hoặc nhập số tiền & nội dung</li>
                  <li>Thanh toán, gói sẽ kích hoạt tự động trong 1-2 phút</li>
                </ol>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigator.clipboard.writeText(qrData.reference)}
                  className="btn-outline flex-1 text-sm"
                >
                  Copy nội dung
                </button>
                <button
                  onClick={handleCloseQr}
                  className="btn-primary flex-1"
                >
                  Đã thanh toán
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: Plan;
  isCurrent: boolean;
  isAdmin: boolean;
  onUpgrade: (planId: string) => void;
  formatPrice: (price: number, cycle: string) => string;
  formatFeatures: (features: string[]) => string[];
}

function PlanCard({ plan, isCurrent, isAdmin, onUpgrade, formatPrice, formatFeatures }: PlanCardProps) {
  const isPopular = plan.id === 'pro';

  return (
    <div className={`card relative p-6 flex flex-col ${isPopular ? 'ring-2 ring-primary scale-105 z-10' : ''} ${isCurrent ? 'ring-2 ring-green-500' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-sm font-medium rounded-full">
          Phổ biến nhất
        </div>
      )}

      {isCurrent && (
        <div className="absolute -top-3 right-3 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
          Đang dùng
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-xl font-bold text-text mb-1">{plan.displayName}</h3>
        <p className="text-text-muted text-sm">{plan.description || ''}</p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-text">{formatPrice(plan.priceMonthly, 'monthly')}</span>
        </div>
        {plan.priceYearly && plan.priceYearly > 0 && (
          <p className="text-sm text-text-muted mt-1">
            Hoặc {formatPrice(plan.priceYearly, 'yearly')} (tiết kiệm {Math.round((1 - plan.priceYearly / (plan.priceMonthly * 12)) * 100)}%)
          </p>
        )}
      </div>

      <ul className="flex-1 space-y-3 mb-6">
        {formatFeatures(plan.features).map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-text">{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => isCurrent ? null : onUpgrade(plan.id)}
        disabled={isCurrent}
        className={`w-full py-3 rounded-lg font-medium transition-colors ${
          isCurrent
            ? 'btn-outline bg-gray-100 text-gray-500 cursor-not-allowed'
            : isPopular
            ? 'btn-primary'
            : 'btn-outline'
        }`}
      >
        {isCurrent ? 'Gói hiện tại' : 'Nâng cấp'}
      </button>

      {isAdmin && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-text-muted text-center">ID: {plan.id}</p>
        </div>
      )}
    </div>
  );
}